import { useEffect, useMemo, useState } from 'react';
import {
    CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import MDButton from '@components/MDButton';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { formatPhone } from '@functions/format-phone';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { CrmLeadService } from '../../kanban-board/services/crm-lead.service';
import { CrmScheduleService } from '../../kanban-board/services/crm-schedule.service';
import { LeadOptionResponse } from '../../kanban-board/services/responses/lead.response';
import { AgendaMeetOption, calendarWarning } from './agenda-meet-option';

interface Props {
    open: boolean;

    /// Horário clicado na grade, em hora local no formato do datetime-local.
    time: string;

    /// Agenda do Google conectada: aparece a opção de criar a sala do Meet.
    googleReady: boolean;

    onCancel: () => void;
    onCreated: () => void;
}

/// Cria ação num horário vago; a agenda mostra compromissos de leads, então exige dizer qual.
export const AgendaNewActionModal = ({ open, time, googleReady, onCancel, onCreated }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [leads, setLeads] = useState<LeadOptionResponse[] | null>(null);
    const [leadId, setLeadId] = useState<string | null>(null);
    const [description, setDescription] = useState('');
    const [when, setWhen] = useState('');
    const [saving, setSaving] = useState(false);
    const [createMeet, setCreateMeet] = useState(false);

    useEffect(() => {
        if (!open) return;

        setLeadId(null);
        setDescription('');
        setWhen(time);
        setCreateMeet(false);
    }, [open, time]);

    // A lista vem inteira ao abrir: o select filtra enquanto se digita e dá para procurar rolando.
    useEffect(() => {
        if (!open || leads !== null) return;

        new CrmLeadService()
            .options()
            .then((res) => setLeads(res.data.data ?? []))
            .catch((err) => {
                setLeads([]);
                showAlert(apiErrorMessage(err), 'error', 4000);
            });
    }, [open, leads, showAlert]);

    /// O servidor ordena pelo mexido por último (teto de 500); na tela, alfabética.
    const options = useMemo(
        () =>
            (leads ?? [])
                .map((lead) => ({
                    value: lead.id,
                    label: `${lead.contactName}${
                        lead.contactPhone ? ` · ${formatPhone(lead.contactPhone)}` : ''
                    }${lead.columnName ? ` · ${lead.columnName}` : ''}`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [leads]
    );

    const save = async () => {
        if (!leadId || !description.trim() || !when) return;

        setSaving(true);

        try {
            const res = await new CrmScheduleService().create({
                leadId,
                description: description.trim(),
                scheduledAt: new Date(when).toISOString(),
                createMeet: googleReady && createMeet,
            });

            const warning = calendarWarning(googleReady, createMeet, res.data.data);
            if (warning) showAlert(t(warning), 'warning', 5000);

            onCreated();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    const field = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary },
        '& .MuiFormLabel-root': { color: cc.textSecondary },
    };

    const label = {
        fontSize: 12,
        fontWeight: 600,
        color: cc.textSecondary,
        display: 'block',
        mb: 0.6,
    };

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
            <DialogTitle sx={{ pb: 0.5 }}>
                {t('agenda.novaAcao')}
                <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                    {t('agenda.novaAcaoAjuda')}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'grid', gap: 2, pt: 2 }}>
                <div>
                    <Typography sx={label}>{t('agenda.lead')}</Typography>
                    <StandaloneAutoCompleteSelect
                        options={options}
                        value={leadId}
                        onChange={(value) => setLeadId((value as string) ?? null)}
                        placeholder={
                            leads === null ? t('general.carregando') : t('agenda.selecioneLead')
                        }
                        disabled={leads === null}
                        isClearable
                    />
                </div>

                <TextField
                    size="small"
                    fullWidth
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    label={t('agenda.oQueFazer')}
                    inputProps={{ maxLength: 200 }}
                    sx={field}
                />

                <TextField
                    size="small"
                    fullWidth
                    type="datetime-local"
                    value={when}
                    onChange={(e) => setWhen(e.target.value)}
                    label={t('agenda.quando')}
                    sx={field}
                />

                {googleReady && <AgendaMeetOption checked={createMeet} onChange={setCreateMeet} />}
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onCancel}>
                    {t('general.cancelar')}
                </MDButton>

                <MDButton
                    variant="gradient"
                    color="success"
                    onClick={save}
                    disabled={!leadId || !description.trim() || !when || saving}
                >
                    {saving ? <CircularProgress size={16} color="inherit" /> : t('general.salvar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
